<template>
  <div class="space-y-4">
    <h3>{{ t('bookingDates') }}</h3>
    <TimeRange
      :booking="booking"
      :disabled="
        disablePriceChangingModules('timeRange') ||
        booking.status === BookingStatus.CAR_RETURNED ||
        booking.status === BookingStatus.FINALIZED
      "
      :is-saving="isSaving"
      class="col-span-2"
      @has-changed="(changed) => (hasChanged.timeRange = changed)"
      @save="emit('save')"
      @cancel="emit('cancel')"
    />
    <Divider />
    <h3>{{ t('bookingLocations') }}</h3>
    <Location
      :booking="booking"
      :dropoff-disabled="
        !$can('update', subject('Booking', booking), 'dropoffLocation') ||
        booking.status === BookingStatus.CAR_RETURNED ||
        booking.status === BookingStatus.FINALIZED ||
        disablePriceChangingModules('location')
      "
      :pickup-disabled="
        !$can('update', subject('Booking', booking), 'pickupLocation') ||
        [BookingStatus.HANDED_OVER, BookingStatus.CAR_RETURNED, BookingStatus.FINALIZED].includes(
          booking.status,
        ) ||
        disablePriceChangingModules('location')
      "
      @has-changed="(changed) => (hasChanged.location = changed)"
      @change-price-preview="pricePreview = $event"
    >
      <template #after>
        <div
          v-if="
            pricePreview.isLoading ||
            pricePreview.transferPrice !== null ||
            pricePreview.totalPrice !== null
          "
          class="text-sm"
        >
          <div v-if="pricePreview.isLoading">{{ t('calculatingPrices') }}</div>
          <div v-else class="grid w-fit grid-cols-[auto_auto] gap-x-2">
            <template v-if="pricePreview.transferPrice !== null">
              <span class="font-medium">{{ t('transferPrice') }}: </span>
              <span v-currency="pricePreview.transferPrice" class="text-right" />
            </template>
            <template v-if="pricePreview.totalPrice !== null">
              <span class="font-medium"
                >{{ t(isLongTerm ? 'contractTotal' : 'newTotalPrice') }}:
              </span>
              <span v-currency="pricePreview.totalPrice" class="text-right" />
            </template>
          </div>
        </div>
      </template>
    </Location>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import TimeRange from './TimeRange.vue';
import type { Booking } from '@/entities/bookings/booking.entity';
import { useI18n } from 'vue-i18n';
import Location from '../booking-forms/Location.vue';
import { subject } from '@casl/ability';
import Divider from '../Divider.vue';
import { BookingStatus } from '@/entities/bookings/booking-status.enum';
import type { PricePreview } from '@/hooks/use-transfer-price-preview';
import { isLongTermPricing } from '@/utils/pricing.utils';

const props = defineProps<{
  booking: Booking;
  isSaving: boolean;
}>();

const isLongTerm = computed(() => isLongTermPricing(props.booking.priceCalculation));

const emit = defineEmits<{
  (e: 'save'): void;
  (e: 'cancel'): void;
}>();

const { t } = useI18n();

const pricePreview = ref<PricePreview>({
  transferPrice: null,
  totalPrice: null,
  isLoading: false,
});

const hasChanged = reactive({
  timeRange: false,
  location: false,
});

const disablePriceChangingModules = (ignore: 'timeRange' | 'location') =>
  Object.entries({
    timeRange: hasChanged.timeRange,
    location: hasChanged.location,
  }).some(([key, value]) => key !== ignore && value);
</script>

<i18n lang="json">
{
  "en": {
    "bookingDates": "Booking dates",
    "bookingLocations": "Booking locations",
    "transferPrice": "Transfer price",
    "newTotalPrice": "New total price",
    "contractTotal": "Contract total",
    "calculatingPrices": "Calculating prices..."
  },
  "de": {
    "bookingDates": "Buchungszeiten",
    "bookingLocations": "Buchungsorte",
    "transferPrice": "Transferpreis",
    "newTotalPrice": "Neuer Gesamtpreis",
    "contractTotal": "Vertragssumme",
    "calculatingPrices": "Preise werden berechnet..."
  },
  "es": {
    "bookingDates": "Fechas de la reserva",
    "bookingLocations": "Ubicaciones de la reserva",
    "transferPrice": "Precio del traslado",
    "newTotalPrice": "Nuevo precio total",
    "contractTotal": "Total del contrato",
    "calculatingPrices": "Calculando precios..."
  }
}
</i18n>
